import { useMemo, useState } from "react";
import { IconLoader2 } from "@tabler/icons-react";
import { Input } from "@/lib/components/Input";
import toast from "@/lib/components/Toast";
import { useDfyOrderStore } from "@/lib/stores/dfy-order-store/dfy-order-store";
import {
  getDfyOrderImageItems,
  getDfyRequiredImageCount,
  isValidDfyListingUrl,
} from "@/lib/stores/dfy-order-store/dfy-order-selectors";
import ImagesDropzone from "../images/ImagesDropzone";
import ImageGrid from "../images/ImageGrid";
import ImageSelectionModeCard from "../components/ImageSelectionModeCard";
import PackageSelectorCard from "../components/PackageSelectorCard";
import { DfyStepProps } from "../dfy-order-form-steps";
import DfyStepHeader from "../components/DfyStepHeader";
import DfyStepNavigation from "../components/DfyStepNavigation";

export default function ImagesStep({ onBack, onNext }: DfyStepProps) {
  const [progress, setProgress] = useState<{
    completed: number;
    total: number;
  } | null>(null);
  const [isListingUrlTouched, setIsListingUrlTouched] = useState(false);

  const details = useDfyOrderStore((state) => state.order?.details ?? null);
  const sourceImages = useDfyOrderStore((state) => state.sourceImages);
  const items = useMemo(
    () => (details ? getDfyOrderImageItems(details, sourceImages) : []),
    [details, sourceImages],
  );
  const uploadedCount = useDfyOrderStore(
    (state) => state.order?.details.sourceImageConfigs.length ?? 0,
  );
  const mode = useDfyOrderStore(
    (state) => state.order?.details.imageSelectionMode ?? "USER",
  );
  const listingUrl = useDfyOrderStore(
    (state) => state.order?.details.listingUrl ?? "",
  );
  const service = useDfyOrderStore((state) => state.service);

  const isChoosingImages = mode === "USER";
  const hasUploads = uploadedCount > 0;
  const hasListingUrl = listingUrl.trim().length > 0;
  const isListingUrlValid = !hasListingUrl || isValidDfyListingUrl(listingUrl);

  const handleAdd = async (files: File[]) => {
    setProgress({ completed: 0, total: files.length });
    await service.uploadImages(files, (completed, total) =>
      setProgress({ completed, total }),
    );
    setProgress(null);
  };

  if (!details) {
    return null;
  }

  const requiredCount = getDfyRequiredImageCount(details);
  const providedCount = isChoosingImages
    ? details.sourceImageConfigs.filter((config) => config.isSelected).length
    : uploadedCount;
  const remainingCount = requiredCount - providedCount;
  const imageLabel = (count: number) => (count === 1 ? "image" : "images");
  const nextConfirmation =
    !hasListingUrl && providedCount < requiredCount
      ? {
          title: `Continue with ${providedCount} ${imageLabel(providedCount)}?`,
          description: isChoosingImages
            ? `Your package includes ${requiredCount} images. You can still select ${remainingCount} ${imageLabel(remainingCount)}. Are you sure you want to continue?`
            : `Your package includes ${requiredCount} images. You can still upload ${remainingCount} ${imageLabel(remainingCount)}. Are you sure you want to continue?`,
        }
      : null;

  return (
    <>
      <DfyStepHeader
        title="Listing images"
        description={`Upload the listing photos or paste a link to the listing. It's helpful to provide all available photos so our editors have more to work with.`}
      />
      <div className="flex flex-col gap-8 py-6">
        <div className="flex flex-col gap-6">
          {!hasUploads && (
            <ImagesDropzone
              onAdd={handleAdd}
              disabled={Boolean(progress) || hasListingUrl}
              label={
                hasListingUrl
                  ? "Our editors will get the images from the listing link. To upload images yourself, clear the link first."
                  : undefined
              }
            />
          )}

          {hasUploads && (
            <ImageGrid
              items={items}
              selectable={isChoosingImages}
              canAdd={!progress}
              onToggleSelect={service.toggleImageSelected}
              onRemove={service.deleteImage}
              onReorder={service.reorderImages}
              onAdd={handleAdd}
            />
          )}

          {progress && (
            <div className="flex items-center gap-2 text-sm text-text-secondary">
              <IconLoader2 className="h-4 w-4 animate-spin" />
              Uploading {progress.completed} of {progress.total}...
            </div>
          )}

          {!hasUploads && (
            <div className="flex flex-col gap-2">
              <label
                htmlFor="listing-url"
                className="text-sm text-text-secondary"
              >
                Or use a listing link instead
              </label>
              <Input
                id="listing-url"
                type="url"
                value={listingUrl}
                onChange={(event) =>
                  service.updateDetails((draft) => {
                    draft.listingUrl = event.target.value;
                  })
                }
                onBlur={() => setIsListingUrlTouched(true)}
                placeholder="https://www.zillow.com/homedetails"
              />
              {isListingUrlTouched && !isListingUrlValid && (
                <p className="text-xs text-red-500">Enter a valid link</p>
              )}
            </div>
          )}

          {hasUploads && (
            <div className="mt-6 flex flex-col gap-6">
              <ImageSelectionModeCard
                mode={mode}
                onSelect={(selectedMode) => {
                  service.updateDetails((draft) => {
                    draft.imageSelectionMode = selectedMode;
                  });
                  if (selectedMode === "USER" && !isChoosingImages) {
                    toast.info(
                      "You can now select the images to use and drag them to reorder.",
                    );
                  }
                }}
              />
              <div className="flex flex-col gap-3">
                <p className="text-sm font-medium">Package</p>
                <PackageSelectorCard
                  type={details.type}
                  onSelect={(packageType) =>
                    service.updateDetails((draft) => {
                      draft.type = packageType;
                    })
                  }
                />
              </div>
            </div>
          )}
        </div>
        <DfyStepNavigation
          onBack={onBack}
          onNext={onNext}
          nextDisabled={
            !isListingUrlValid || (!hasListingUrl && providedCount === 0)
          }
          nextConfirmation={nextConfirmation}
        />
      </div>
    </>
  );
}
